<template>
  <article class="main-app-content">
    <AuthPopup />
    
    <div class="modals">

      <!-- Create Bot Modal Window -->
      <ModalWindow @modalClickOnBackground="closeCreateBotModal" :modalOpen="modalsState.createOpen">
        <div class="title">Create Bot</div>

        <div class="input">
          <BasicInput v-model.trim="newBot.name" 
            placeholder="Type your new Bot Name" 
            class="super-wide new-bot-name" 
            id="newBotInput"
            @keyup.native.enter="createBotButton" />
          <div class="error" v-if="newBot.error">{{ newBot.error }}</div>
        </div>
        <div class="company-selector-container">
          <select name="companies" v-model="newBot.companyId" class="company-selector">
            <option v-for="company in editableCompanies" :key="company.id" :value="company.id">{{company.name}}</option>
          </select>
        </div>
        
        <div class="actions">
          <BotActionButton class="blue shadow" @click="createBotButton">Create</BotActionButton>
          <BotActionButton class="gray shadow" @click="closeCreateBotModal">Cancel</BotActionButton>
        </div>
      </ModalWindow>

      <!-- Edit Bot Company Modal Window -->
      <ModalWindow @modalClickOnBackground="closeEditCompanyModal" :modalOpen="modalsState.editCompanyOpen" class="company-selector-modal">
        <div class="title">Select Company</div>

        <div class="company-selector-container">

          <select name="companies" v-model="selectedCompany" class="company-selector">
            <option v-for="company in editableCompanies" :key="company.id" :value="company.id">{{company.name}}</option>
          </select>

        </div>

        <div class="actions">
          <BotActionButton class="blue shadow" @click="confirmCompanyButton">Confirm</BotActionButton>
          <BotActionButton class="gray shadow" @click="closeEditCompanyModal">Cancel</BotActionButton>
        </div>
      </ModalWindow>

      <!-- Delete Bot Modal Window -->
      <ModalWindow @modalClickOnBackground="closeDeleteBotModal" :modalOpen="modalsState.deleteOpen" class="delete-bot-modal">
        <div class="title">Delete bot</div>
        <p class="text">
          Are you sure you want to delete <span class="highlight">"{{selectedBot.botName}}"</span> bot?
        </p>
        <div class="actions">
          <BotActionButton class="red shadow" @click="deleteSelectedBot">Delete</BotActionButton>
          <BotActionButton class="gray shadow" @click="closeDeleteBotModal">Cancel</BotActionButton>
        </div>
      </ModalWindow>

    </div>
    
    <!-- Bot List -->

    <transition name="fade">
      <div v-if="filteredList.length > 0" class="bot-list bot-grid">
        <div v-for="(bot, index) in filteredList" :key="bot.id" class="bot">
          <div class="bot-icon">
            <router-link :to="'/bot/' + bot.id" tag="div">
              <img :src="botIconsList[index]" :alt="bot.botName" class="bot-icon-image" />
            </router-link>

            <ContextMenu v-if="bot.canEdit">
              <div @click="openBotCompanyModal(index)" class="cm-item">
                Change company
              </div>
              <div @click="openDeleteBotModal(index)" class="cm-item red">
                Delete bot
              </div>
            </ContextMenu>
          </div>

          <router-link :to="'/bot/' + bot.id" tag="div" class="bot-name">{{bot.botName}}</router-link>

          <div class="last-edited">Last edited: {{ getLastEditedFromLastUpdate(bot.updated) }}</div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div class="no-bots" v-show="filteredList.length === 0">
      <h1>Oops...</h1>
      <h2>Looks like there are no bots here</h2>
    </div>
    </transition>

  </article>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BotActionButton from "@/components/buttons/BotActionButton";
import AuthPopup from "@/components/app/general/AuthPopup";
import Trash from "@/components/icons/Trash";
import ContextMenu from "@/components/app/general/ContextMenu";
import ModalWindow from "@/components/app/general/ModalWindow";
import BasicInput from "@/components/inputs/BasicInput";
import moment from "moment";
import tz from "moment-timezone";
import helpers from "@/helpers";
import validators from "@/validators";

export default {
  data: () => {
    return {
      newBot: {
        name: "",
        companyId: 0,
        error: ""
      },
      search: "",
      iconsList: [],
      modalsState: {
        editCompanyOpen: false,
        deleteOpen: false,
        createOpen: false
      },
      selectedBot: {}
    };
  },
  computed: {
    ...mapGetters(["allBots", "selectedCompany", "allCompanies", "editableCompanies"]),
    filteredList() {
      if (this.allBots.length > 0) {
        const botsForCompany = this.allBots.filter((bot) => {
          return this.selectedCompany ? bot.companyId === this.selectedCompany : true;
        });
        return botsForCompany.filter((bot) => {
          return bot.botName.toLowerCase().includes(this.search.toLowerCase());
        });
      } else {
        return [];
      }
    },
    botIconsList() {
      return this.generateIconsList(this.allBots.length);
    },
  },
  mounted() {
    this.fetchBots();

    this.$root.$on("searchBots", (searchValue) => {
      this.search = searchValue;
    });

    this.$root.$on("openCreateBotPopup", () => {
      this.modalsState.createOpen = true;
      this.newBot.companyId = this.editableCompanies[0].companyId;
      helpers.focusOnInputAfterModalOpen("#newBotInput", 400);
    });
  },
  methods: {
    ...mapActions(["fetchBots", "deleteBot", "createBot", "updateBotCompany"]),
    setDeleteIntention(index, value) {
      this.$set(this.allBots[index], "deleteIntention", value);
    },
    isCompanySelected(id) {
      return this.selectedCompany === id;
    },
    confirmBotDeletion(index) {
      if (this.allBots[index].deleteIntention) {
        const result = this.deleteBot(this.allBots[index].id);
      }
    },
    generateIconsList(count = 8) {
      const iconsCount = 8;
      let currentCounter = 1;
      let list = [];
      let publicPath = process.env.BASE_URL;
      for (let i = 0; i < count; i++) {
        let iconUrl = publicPath + "images/botImages/Pattern " + currentCounter + ".png";
        list.push(iconUrl);
        currentCounter++;
        if (currentCounter > iconsCount) {
          currentCounter = 1;
        }
      }
      return list;
    },
    getLastEditedFromLastUpdate(last_update) {
      return moment(last_update).fromNow();
    },
    openBotCompanyModal(index) {
      this.selectBot(index);
      this.selectedCompany = this.selectedBot.companyId;
      this.modalsState.editCompanyOpen = true;
    },
    closeEditCompanyModal() { this.modalsState.editCompanyOpen = false },
    openDeleteBotModal(index) {
      this.selectBot(index);
      this.modalsState.deleteOpen = true;
    },
    closeDeleteBotModal() { this.modalsState.deleteOpen = false },
    closeCreateBotModal() { this.modalsState.createOpen = false },
    selectBot(index) { this.selectedBot = this.filteredList[index] },
    async confirmCompanyButton() {
      let data = {
        botId: this.selectedBot.id,
        companyId: this.selectedCompany
      };
      const result = await this.updateBotCompany(data);
      if (result) {
        setTimeout(() => {
          this.modalsState.editCompanyOpen = false;
        }, 150);   
      } else {
        console.error("Something went wrong while saving company")
      }
    },
    async deleteSelectedBot() {
      if (this.selectedBot.id) {
        await this.deleteBot(this.selectedBot.id);
        this.closeDeleteBotModal();
      }
    },
    async createBotButton() {
      this.newBot.name = this.newBot.name.trim();
      const {valid, error} = validators.isBotNameValid(this.newBot.name);
      if (valid) {
        const data = {
          name: this.newBot.name,
          companyId: this.newBot.companyId
        }
        const result = await this.createBot(data);
        if (result) {
          setTimeout(() => {
            this.modalsState.createOpen = false;
            setTimeout(() => {
              this.$router.push("/bot/" + result);
            }, 150);
          }, 75);
        }
      } else {
        this.newBot.error = error;
      }
    }
  },
  components: { AuthPopup, ContextMenu, ModalWindow, BasicInput, BotActionButton},
};
</script>

<style lang="scss">
$textColorLightGray: rgba(
  $color: #fff,
  $alpha: 0.85,
);
$colorOrange: #fbb442;

.modals {
  height: 0;
  width: 0;

  .new-bot-name {
    font-size: 14px;
  }

  .company-selector-modal {
    .title {
      margin: 48px auto 12px;
    }
    .actions {
      margin-bottom: 40px;
    }
  }

  .company-selector-container {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 16px;
      .company-selector {
        width: 100%;
        max-height: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        border-bottom: 1px solid rgba($color: #555555, $alpha: 0.4);
        font-size: 16px;
        background-color: #4D4D4D;
        color: #fff;
        padding: 8px 10px;
        outline: none;
        border: 1px solid #686868;
        border-radius: 4px;
        max-width: 400px;
      }
    }
  
}

.main-app-content {
  width: 100%;
  padding: 55px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow-y: scroll;
  background-color: #3b3b3b;

  .modals {
    height: 0;
    width: 0;
  }

  .no-bots {
    text-align: center;
    h1 {
      font-size: 32px;
      font-weight: 500;
    }
    h2 {
      font-size: 24px;
      font-weight: 400;
    }
  }

  .bot-grid {
    width: auto;
    display: grid;
    grid-gap: 55px 55px;
    //grid-template-columns: 180px 180px 180px 180px;
    align-content: start;
    @media (min-width: 720px) {
      grid-template-columns: 180px 180px;
    }
     @media (min-width: 980px) {
      grid-template-columns: 180px 180px 180px;
    }
    @media (min-width: 1280px) {
      grid-template-columns: 180px 180px 180px 180px;
    }
    @media (min-width: 1600px) {
      grid-template-columns: 180px 180px 180px 180px 180px;
    }
    @media (min-width: 1920px) {
      grid-template-columns: 180px 180px 180px 180px 180px 180px;
    }
  }

  .bot-list {  
    .bot {
      width: 180px;

      .bot-icon {
        height: 180px;
        border: 2px solid #5b5757;
        border-radius: 2px;
        margin-bottom: 8px;
        cursor: pointer;
        overflow: hidden;
        box-shadow: 0 0px 0px rgba(0, 0, 0, 0.16), 0 0px 0px rgba(0, 0, 0, 0.23);
        transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);

        .bot-icon-image {
          width: 100%;
          height: 100%;
          transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
          transform: scale(1.01);
        }

        .cm-container {
          opacity: 0;
          transition: opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
          position: relative;
          top: -180px;

          .cm-button {
            margin: 8px 8px 4px 8px;
            background-color: #515151;
            border: 1px solid #777777;
            border-radius: 2px;
            position: relative;
            left: 134px;
            &.open {
              border: none;
            }
          }
          .cm-menu {
            margin: 0;
            position: relative;
            left: 40px;
          }
        }
      }

      .bot-name {
        width: 100%;
        color: #ffffff;
        font-size: 16px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        opacity: 0.85;
        margin-bottom: 6px;
        transition: opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
        cursor: pointer;
      }

      .last-edited {
        font-size: 12px;
        color: #a1a1a1;
      }

      &:hover {
        .bot-name {
          opacity: 1;
        }
        .bot-icon {
          border-color: #fff;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16), 0 4px 8px rgba(0, 0, 0, 0.23);

          .cm-container {
            opacity: 1;
          }
          .bot-icon-image {
            transform: scale(1.25);
          }
        }
      }
    } 
  }
}
</style>
